import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  DollarSign, 
  Activity, 
  ArrowUp,
  Package,
  FileText,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';

export default function DashboardPage() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate data loading
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  // Mock Data - Enhanced for Cannabis Compliance Platform
  const stats = {
    totalOperators: 1247,
    activeLicenses: 892,
    inventoryValue: 4825000,
    complianceScore: 94.5,
    pendingReports: 12,
    expiringLicenses: 8,
    pendingCOAs: 15,
    inventoryUnits: 45230
  };

  const complianceHealthData = [
    { name: 'Compliant', value: 842, color: '#10b981' },
    { name: 'Warning', value: 34, color: '#f59e0b' },
    { name: 'Non-Compliant', value: 16, color: '#ef4444' },
  ];

  const revenueData = [
    { month: 'Jan', revenue: 385000, inventory: 420000 },
    { month: 'Feb', revenue: 415000, inventory: 445000 },
    { month: 'Mar', revenue: 445000, inventory: 467000 },
    { month: 'Apr', revenue: 475000, inventory: 489000 },
    { month: 'May', revenue: 495000, inventory: 510000 },
    { month: 'Jun', revenue: 525000, inventory: 534000 },
  ];

  const inventoryTrendData = [
    { month: 'Jan', flower: 12500, edibles: 8200, concentrates: 5400 },
    { month: 'Feb', flower: 13200, edibles: 8800, concentrates: 5800 },
    { month: 'Mar', flower: 14100, edibles: 9200, concentrates: 6100 },
    { month: 'Apr', flower: 13800, edibles: 9500, concentrates: 6400 },
    { month: 'May', flower: 14500, edibles: 9800, concentrates: 6700 },
    { month: 'Jun', flower: 15200, edibles: 10200, concentrates: 7100 },
  ];

  const licenseExpirationData = [
    { name: 'Within 30 Days', value: 8, color: '#ef4444' },
    { name: '31-60 Days', value: 15, color: '#f59e0b' },
    { name: '61-90 Days', value: 23, color: '#10b981' },
    { name: '90+ Days', value: 846, color: '#6366f1' },
  ];

  const recentAlerts = [
    {
      id: 1,
      type: 'warning',
      title: 'License Expiring Soon',
      description: 'Cultivation License #CL-2024-089 expires in 15 days',
      time: '2 hours ago'
    },
    {
      id: 2,
      type: 'error',
      title: 'Missing COA',
      description: 'Batch #BT-45823 requires certificate of analysis',
      time: '5 hours ago'
    },
    {
      id: 3,
      type: 'warning',
      title: 'Low Inventory Alert',
      description: 'Blue Dream strain below minimum threshold',
      time: '1 day ago'
    },
    {
      id: 4,
      type: 'success',
      title: 'Compliance Report Submitted',
      description: 'Monthly state report successfully filed',
      time: '2 days ago'
    },
  ];

  const recentActivity = [
    { action: 'New License Approved', entity: 'Springfield Dispensary', time: '30 mins ago' },
    { action: 'Inventory Transfer', entity: 'Batch #BT-45891', time: '1 hour ago' },
    { action: 'COA Uploaded', entity: 'Lab Test #LT-9823', time: '3 hours ago' },
    { action: 'Compliance Report Filed', entity: 'Monthly Report - June', time: '5 hours ago' },
    { action: 'License Renewed', entity: 'Green Valley Cultivation', time: '1 day ago' },
  ];

  const statCards = [
    {
      title: 'Total Operators',
      value: stats.totalOperators.toLocaleString(),
      change: '+12.5%',
      changeType: 'positive',
      icon: Users,
      description: 'Total registered operators',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Active Licenses',
      value: stats.activeLicenses.toLocaleString(),
      change: '+8.2%',
      changeType: 'positive',
      icon: ShieldCheck,
      description: 'Currently active licenses',
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Inventory Value',
      value: `$${(stats.inventoryValue / 1000000).toFixed(2)}M`,
      change: '+15.3%',
      changeType: 'positive',
      icon: Package,
      description: 'Total inventory value',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      title: 'Compliance Score',
      value: `${stats.complianceScore}%`,
      change: '+2.4%',
      changeType: 'positive',
      icon: CheckCircle,
      description: 'Overall compliance rating',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
  ];

  const quickStats = [
    {
      label: 'Pending Reports',
      value: stats.pendingReports,
      icon: FileText,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      label: 'Expiring Licenses',
      value: stats.expiringLicenses,
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    },
    {
      label: 'Pending COAs',
      value: stats.pendingCOAs,
      icon: Clock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    {
      label: 'Inventory Units',
      value: stats.inventoryUnits.toLocaleString(),
      icon: Package,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50'
    },
  ];

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dashboard Overview</h1>
          <p className="text-muted-foreground mt-1">Monitor your cannabis operations and compliance status</p>
        </div>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardDescription className="text-sm font-medium">
                  {stat.title}
                </CardDescription>
                <div className={`p-2 ${stat.bgColor} rounded-lg`}>
                  <stat.icon className={`h-5 w-5 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground">{stat.value}</div>
                <div className="flex items-center mt-1">
                  <Badge
                    variant={stat.changeType === 'positive' ? 'success' : 'destructive'}
                    className="text-xs"
                  >
                    <ArrowUp className="w-3 h-3 mr-1" />
                    {stat.change}
                  </Badge>
                  <span className="text-xs text-muted-foreground ml-2">
                    from last month
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Stats Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickStats.map((stat, index) => (
            <Card key={index}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                    <p className="text-2xl font-bold mt-1">{stat.value}</p>
                  </div>
                  <div className={`p-3 ${stat.bgColor} rounded-lg`}>
                    <stat.icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Compliance Health & Revenue Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Compliance Health */}
          <Card>
            <CardHeader>
              <CardTitle>Compliance Health</CardTitle>
              <CardDescription>Overall operator compliance status</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={complianceHealthData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {complianceHealthData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {complianceHealthData.map((item, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                      <span>{item.name}</span>
                    </div>
                    <span className="font-medium">{item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Revenue & Inventory Trend */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Revenue & Inventory Trend</CardTitle>
              <CardDescription>Financial performance over last 6 months</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorInventory" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="revenue" stroke="#10b981" fillOpacity={1} fill="url(#colorRevenue)" />
                  <Area type="monotone" dataKey="inventory" stroke="#6366f1" fillOpacity={1} fill="url(#colorInventory)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Inventory Trend & License Expiration */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Inventory by Category */}
          <Card>
            <CardHeader>
              <CardTitle>Inventory by Category</CardTitle>
              <CardDescription>Product distribution trends</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={inventoryTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="flower" stroke="#10b981" strokeWidth={2} />
                  <Line type="monotone" dataKey="edibles" stroke="#f59e0b" strokeWidth={2} />
                  <Line type="monotone" dataKey="concentrates" stroke="#6366f1" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* License Expiration Timeline */}
          <Card>
            <CardHeader>
              <CardTitle>License Expiration Timeline</CardTitle>
              <CardDescription>Licenses by expiration period</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={licenseExpirationData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={100} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                    {licenseExpirationData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Alerts & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Alerts */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Alerts</CardTitle>
              <CardDescription>System notifications and warnings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentAlerts.map((alert) => (
                  <div key={alert.id} className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                    <div className="mt-0.5">
                      {alert.type === 'error' && <XCircle className="h-5 w-5 text-red-600" />}
                      {alert.type === 'warning' && <AlertTriangle className="h-5 w-5 text-orange-600" />}
                      {alert.type === 'success' && <CheckCircle className="h-5 w-5 text-green-600" />}
                    </div>
                    <div className="flex-1 space-y-1">
                      <p className="text-sm font-medium">{alert.title}</p>
                      <p className="text-xs text-muted-foreground">{alert.description}</p>
                      <p className="text-xs text-muted-foreground">{alert.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest system events</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                    <div className="w-2 h-2 mt-2 rounded-full bg-primary"></div>
                    <div className="flex-1 space-y-1">
                      <p className="text-sm font-medium">{activity.action}</p>
                      <p className="text-xs text-muted-foreground">{activity.entity}</p>
                      <p className="text-xs text-muted-foreground">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
